const { logWarn, logError, logDebug } = require('./lib/logger');
const { loadScript, loadScriptWithin } = require('./lib/scriptLoader');
const { ensurePrebidFallback, PREBID_LOAD_FAILSAFE_MS } = require('./lib/prebidFallback');
const { configurePrebid } = require('./lib/prebidConfig');
const { setupAutoRefresh } = require('./lib/autoRefresh');
const { applyContentAdSticky } = require('./lib/contentAdSticky');
const { createPageAdRuntime } = require('./lib/adRuntime');
const { applySlotSizeFromPrebid, ensureAdSlotMinHeight } = require('./lib/adSizeUtils');
const { onElementInViewport } = require('./lib/viewportObserver');
const { resolveCoordinatedRequestThreshold } = require('./lib/adViewability');
const { createNewsletterIframeModule } = require('./lib/newsletter');
const { createMemberSectionAd } = require('./lib/memberAds');
const { injectModulesBeforeSectionTitles } = require('./lib/sectionInjection');
const {
  buildHomepageGamTargeting,
  applyGamTargeting
} = require('./lib/gamTargeting');
const {
  applyPrebidTargetingToSlots,
  applySlotTargeting,
  configurePageSettings,
  enableLazyLoad
} = require('./lib/gptConfig');
const {
  createHomepageInfeedAdInserter,
  syncPlacedAdCountFromDom,
  setupHomepageInfeedAdsObserver,
  getHomepageInfeedConfig,
  isHomepageInfeedSlotId,
  collapseHomepageInfeedAdSlot,
  restoreHomepageInfeedAdSlot
} = require('./lib/homepageInfeedAds');

const {
  resolveLanguage,
  getSlotPath,
  emitAdEvent
} = require('./adsBrandUtils');
const {
  applySkinAdRenderResult,
  hideTopAdWrapper,
  injectTopAndSkinAdContainers,
  isSkinAdEligible,
  prepareSkinAdSlot,
  shouldHideTopAdWrapper
} = require('./lib/desktopArticle');

// Ad unit factories
const {
  HOMEPAGE_AD_SIZES,
  generateHomepageDesktopAdUnits,
  makeHomepageDtContentAdUnit,
  makeHomepageTopAdUnit,
  ensurePrebidAdUnitForCode
} = require('./adUnitFactory');

function createHomepageDesktopAds(config, options = {}) {
  const targeting = options.targeting || {};

  return function initHomepageDesktopAds(ctx) {
    const {
      prebidUrl,
      gptUrl,
      _noads,
      _urlonly,
      _sponsored,
      _fr_lang,
      fwjson,
      userIds,
      pbjs,
      googletag,
      pro,
      emitter,
      isLoggedIn,
      pageType,
      auctionStrategy,
      adDiagnosticsEnabled,
      device,
      pageKey
    } = ctx;

    const brand = config.brand;
    const language = resolveLanguage(config, _fr_lang);
    const PREBID_TIMEOUT = 1500;
    const FAILSAFE_TIMEOUT = 3000;
    const pageTargeting = buildHomepageGamTargeting({
      fwjson,
      targeting,
      language
    });
    const disableTopAd = typeof window !== 'undefined' && window._millaDisableTopAd === true;
    const skinEnabled = isSkinAdEligible(
      config,
      typeof window !== 'undefined'
        ? (window.innerWidth || document.documentElement.clientWidth || 0)
        : 0
    );

  // Use sizes from factory
  const topAdSizes = HOMEPAGE_AD_SIZES.desktop.topAd;
  const dtContentSizes = HOMEPAGE_AD_SIZES.desktop.content;
  const skinSize = HOMEPAGE_AD_SIZES.desktop.skin;
  const CONTENT_SLOT_MIN_HEIGHT = 280;

    function ensureHomepageSkinContainer() {
      if (typeof document === 'undefined') return;
      if (_noads === 'yes' || _urlonly === 'yes' || pro === 'active') return;
      if (!skinEnabled) return;
      if (document.getElementById('skin-ad')) return;

      const skinAd = document.createElement('div');
      skinAd.id = 'skin-ad';
      skinAd.className = 'ad-slot';

      const topAdWrapper = document.querySelector('.top-ad-wrapper');
      if (topAdWrapper && topAdWrapper.parentElement) {
        topAdWrapper.parentElement.insertBefore(skinAd, topAdWrapper.nextSibling);
      } else if (document.body) {
        document.body.appendChild(skinAd);
      }
    }

    function scheduleSectionTitleEmbeds() {
      if (typeof document === 'undefined') return;
      if (_noads === 'yes' || _urlonly === 'yes' || _sponsored === 'yes' || pro === 'active') return;

      const resolvedPageType = pageType === 'section' ? 'section' : 'homepage';

    const inject = () => {
      try {
        if (isLoggedIn) {
          injectModulesBeforeSectionTitles({
            moduleKey: 'member-ad',
            locationPrefix: `${resolvedPageType}_section_title`,
            maxInsertions: 2,
            // The Feed is the third Narcity heading and fourth MTL Blog heading.
            // Section injection deduplicates MTL Blog's existing Feed placement.
            preferredIndices: resolvedPageType === 'homepage' ? [brand === 'mtlblog' ? 3 : 2, 3] : [1, 3],
            observe: true,
            createModule: () => createMemberSectionAd(ctx, { slotPrefix: 'dt-content-ad', minHeight: CONTENT_SLOT_MIN_HEIGHT })
          });
        } else if (pro !== 'active') {
          injectModulesBeforeSectionTitles({
            moduleKey: 'newsletter',
            locationPrefix: `${resolvedPageType}_section_title`,
            maxInsertions: 1,
            preferredIndices: [3],
            observe: true,
            createModule: (location) => createNewsletterIframeModule(location)
          });
        }
      } catch (err) {
        logWarn('Failed to inject newsletter boxes/member ads above section titles (desktop)', err);
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', inject);
    } else {
      inject();
    }

    // Allow for late-rendered section blocks (Rebelmouse).
    setTimeout(inject, 1500);
    setTimeout(inject, 4500);
  }

    scheduleSectionTitleEmbeds();

    if (_noads === 'yes' || _urlonly === 'yes' || pro === 'active') {
      if (shouldHideTopAdWrapper(_noads, pro)) {
        hideTopAdWrapper();
      }
      emitAdEvent(emitter, brand, 'ready');
      return;
    }

    if (disableTopAd) {
      hideTopAdWrapper();
    }

    ensureHomepageSkinContainer();

  function initializeHomepageDesktopAds() {
    const adRuntime = createPageAdRuntime({
      brand,
      pageType: pageType || 'homepage',
      device: device || 'desktop',
      pageKey,
      auctionStrategy,
      diagnosticsEnabled: adDiagnosticsEnabled,
      pbjs,
      googletag,
      timeoutMs: PREBID_TIMEOUT
    });
    const coordinator = adRuntime.coordinator;
    const contentAdRequestThreshold = resolveCoordinatedRequestThreshold({
      coordinated: adRuntime.coordinated,
      legacyThreshold: 1400,
      device: device || 'desktop'
    });
    // Generate ad units using factory
    const adUnits = adRuntime.coordinated
      ? []
      : generateHomepageDesktopAdUnits(config, { contentCount: 12 });
    const enabledAdUnits = disableTopAd
      ? adUnits.filter((unit) => unit && unit.code !== 'top-ad')
      : adUnits;

    pbjs.que.push(function () {
      configurePrebid(pbjs, userIds);
      if (adRuntime.coordinated) {
        return;
      }
      pbjs.addAdUnits(enabledAdUnits);
      pbjs.requestBids({
        timeout: PREBID_TIMEOUT,
        bidsBackHandler: function () {
          initAdserver();
        }
      });
    });

    function initAdserver() {
      if (!pbjs || typeof pbjs.getBidResponses !== 'function' || pbjs.initAdserverSet) {
        return;
      }
      pbjs.initAdserverSet = true;
    }

    if (!adRuntime.coordinated) {
      setTimeout(initAdserver, FAILSAFE_TIMEOUT);
    }

    googletag.cmd.push(function () {
      const REFRESH_KEY = 'refresh';
      const REFRESH_VALUE = 'true';
      const staticSlots = [];
      adRuntime.observeGpt(googletag.pubads());

      const pubads = googletag.pubads();

      if (!adRuntime.coordinated) {
        enableLazyLoad(googletag, pubads, {
          fetchMarginPercent: 120,
          renderMarginPercent: 20,
          mobileScaling: 2.0
        });
      }

      pubads.addEventListener('slotRenderEnded', function (event) {
        const slot = event.slot;
        const slotId = slot.getSlotElementId && slot.getSlotElementId();
        const div = slotId ? document.getElementById(slotId) : null;
        const size = event.size;
        const isInfeedSlot = isHomepageInfeedSlotId(slotId, 'dt-content-ad');

        if (isInfeedSlot && event.isEmpty && div) {
          if (collapseHomepageInfeedAdSlot(div)) {
            return;
          }
        }

        if (isInfeedSlot && !event.isEmpty && div) {
          restoreHomepageInfeedAdSlot(div);
        }

        if (slotId === 'skin-ad' && applySkinAdRenderResult(div, event)) {
          return;
        }

        if (size && size.length === 2 && div) {
          if (size && !(size[0] === 0 && size[1] === 0)) {
            if (
              size[0] === 1 ||
              (size[0] === 2000 && (size[1] === 1 || size[1] === 250))
            ) {
              div.style.height = 'auto';
            } else if (size[0] === 2000 && size[1] === 1200) {
              div.style.height = '0px';
            } else {
              div.style.height = `${size[1]}px`;
              div.style.width = `${size[0]}px`;
            }
          } else if (size[0] === 0 && size[1] === 0) {
            div.style.width = 'auto';
            div.style.height = 'auto';
          }
          div.style.margin = 'auto';
        }

        if (isInfeedSlot && !event.isEmpty && div) {
          applyContentAdSticky(div, size);
        }

        // Reset container size if slot is empty (GPT returned no ad)
        // Keep min-height to avoid CLS when empty creatives collapse.
        if (event.isEmpty && div) {
          div.style.height = 'auto';
        }
      });

      applyGamTargeting(googletag, pageTargeting);

      // PPID = the derived, consent-gated ads id only (2026-09-09); no sharedId fallback.
      if (window.ppid) {
        pubads.setPublisherProvidedId(window.ppid);
      }

      configurePageSettings(googletag, pubads, {
        disableInitialLoad: true,
        singleRequest: true,
        collapseDiv:
          typeof window !== 'undefined' && window._millaShowAdPlaceholders === true
            ? null
            : 'ON_NO_FILL'
      });
      googletag.enableServices();

      setupAutoRefresh(googletag, pbjs, {
        refreshKey: REFRESH_KEY,
        refreshValue: REFRESH_VALUE,
        prebidTimeout: PREBID_TIMEOUT,
        emptyInitialDelayMs: adRuntime.coordinated ? 15000 : undefined,
        emptyDelayMs: adRuntime.coordinated ? 30000 : 10000,
        filledDelayMs: 30000,
        viewabilityThreshold: 0.5,
        viewabilityTimeMs: 1000,
        maxRefreshes: 10,
        maxEmptyRefreshes: adRuntime.coordinated ? 2 : undefined,
        maxFilledRefreshes: adRuntime.coordinated ? 10 : undefined,
        preloadMarginPx: adRuntime.coordinated ? 0 : undefined,
        coordinator,
        shouldApplyPrebidSize: (slotId) => slotId !== 'top-ad',
        shouldManageSlot: function (slotId) {
          if (disableTopAd && slotId === 'top-ad') return false;
          return slotId === 'top-ad' || slotId.indexOf('dt-content-ad-') === 0;
        }
      });

      // Skin slot using config
      if (skinEnabled) {
        prepareSkinAdSlot(document.getElementById('skin-ad'));
        const skinSlotPath = getSlotPath(config, 'desktop', 'skin');
        const skinSlot = googletag
          .defineSlot(skinSlotPath, skinSize, 'skin-ad')
          .addService(pubads);
        googletag.display('skin-ad');
        staticSlots.push(skinSlot);
      }

      if (staticSlots.length > 0) {
        googletag.pubads().refresh(staticSlots);
      }

      if (!disableTopAd) {
        // Top ad slot using config
        const topAdPath = getSlotPath(config, 'desktop', 'topAd');
        const topAdSlot = applySlotTargeting(
          googletag.defineSlot(topAdPath, topAdSizes, 'top-ad'),
          {
            lazyload: 'lazyload',
            [REFRESH_KEY]: REFRESH_VALUE
          }
        ).addService(pubads);

        if (coordinator) {
          coordinator.registerSlot({
            slotId: 'top-ad',
            gptSlot: topAdSlot,
            createAdUnit: () => makeHomepageTopAdUnit(config),
            placement: { slotFamily: 'top', slotIndex: 1 },
            applyPrebidSize: false
          });
          coordinator.requestInitial('top-ad');
        } else {
          pbjs.requestBids({
            timeout: PREBID_TIMEOUT,
            adUnitCodes: ['top-ad'],
            bidsBackHandler: function () {
              googletag.cmd.push(function () {
                applyPrebidTargetingToSlots(pbjs, topAdSlot);
                googletag.pubads().refresh([topAdSlot]);
              });
            }
          });
        }
      }

      // Content ad slot path using config
      const contentSlotPath = getSlotPath(config, 'desktop', 'content');

      onElementInViewport({
        selector: '.content-ad',
        onIntersect: function (entry) {
          const slotId = entry && entry.target && entry.target.id;
          if (!slotId) return;
          logDebug('slot:define', {
            slotId,
            adUnit: contentSlotPath
          });
          const adSlot = applySlotTargeting(
            googletag.defineSlot(contentSlotPath, dtContentSizes, slotId),
            {
              lazyload: 'lazyload',
              [REFRESH_KEY]: REFRESH_VALUE
            }
          ).addService(pubads);
          logDebug('slot:display', { slotId });
          googletag.display(slotId);

          if (coordinator) {
            const slotIndex = Number((slotId.match(/-(\d+)$/) || [])[1]);
            coordinator.registerSlot({
              slotId,
              gptSlot: adSlot,
              createAdUnit: () => makeHomepageDtContentAdUnit(slotIndex, config),
              placement: { slotFamily: 'content', slotIndex }
            });
            coordinator.requestInitial(slotId);
          } else {
            pbjs.que.push(function () {
              try {
                // Use shared factory to ensure Prebid ad unit exists
                ensurePrebidAdUnitForCode(pbjs, slotId, config, 'desktop');
              } catch (err) {
                logWarn('Failed to ensure Prebid ad unit for slot', err);
              }

              logDebug('slot:requestBids', { slotId, timeout: PREBID_TIMEOUT });
              pbjs.requestBids({
                timeout: PREBID_TIMEOUT,
                adUnitCodes: [slotId],
                bidsBackHandler: function () {
                  googletag.cmd.push(function () {
                    logDebug('slot:bidsBack', { slotId });
                    applySlotSizeFromPrebid(slotId, pbjs);
                    applyPrebidTargetingToSlots(pbjs, adSlot);
                    logDebug('slot:refresh', { slotId, reason: 'initial' });
                    googletag.pubads().refresh([adSlot]);
                  });
                }
              });
            });
          }
        },
        threshold: contentAdRequestThreshold,
        oneTime: true
      });

    });

    // Tablet sticky bottom ad (769-1024px) - manual fixed positioning
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    if (viewportWidth > 768 && viewportWidth <= 1024) {
      // Create sticky container
      const stickyContainer = document.createElement('div');
      stickyContainer.id = 'milla-tablet-sticky-bottom';
      stickyContainer.style.cssText = `
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        z-index: 9999;
        background: #fff;
        box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
        text-align: center;
        padding: 8px 0;
      `;

      // Create ad container
      const adContainer = document.createElement('div');
      adContainer.id = 'tablet-sticky-ad';
      adContainer.style.cssText = 'display: inline-block; margin: 0 auto;';
      stickyContainer.appendChild(adContainer);

      // Create close button
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = '&times;';
      closeBtn.style.cssText = `
        position: absolute;
        top: -12px;
        right: 10px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 1px solid #ccc;
        background: #fff;
        font-size: 16px;
        line-height: 1;
        cursor: pointer;
        z-index: 10000;
      `;
      closeBtn.onclick = function () {
        stickyContainer.style.display = 'none';
      };
      stickyContainer.appendChild(closeBtn);

      document.body.appendChild(stickyContainer);

      // Define and display the ad slot with custom refresh logic
      googletag.cmd.push(function () {
        const topAdPath = `${config.gamNetwork}/${config.slots.desktop.topAd}`;
        const stickySlot = applySlotTargeting(
          googletag.defineSlot(topAdPath, HOMEPAGE_AD_SIZES.desktop.tabletSticky, 'tablet-sticky-ad'),
          {
            lazyload: 'lazyload',
            refresh: 'true'
          }
        ).addService(googletag.pubads());

        let stickyRefreshTimer = null;
        let stickyIsEmpty = true;
        const EMPTY_REFRESH_MS = 5000;
        const FILLED_REFRESH_MS = 30000;

        function refreshStickyAd() {
          if (stickyContainer.style.display === 'none') {
            return;
          }
          googletag.pubads().refresh([stickySlot]);
        }

        function scheduleStickyRefresh() {
          if (stickyRefreshTimer) {
            clearTimeout(stickyRefreshTimer);
          }
          const delay = stickyIsEmpty ? EMPTY_REFRESH_MS : FILLED_REFRESH_MS;
          stickyRefreshTimer = setTimeout(function () {
            refreshStickyAd();
          }, delay);
        }

        // Listen for render events on this slot
        googletag.pubads().addEventListener('slotRenderEnded', function (event) {
          if (event.slot === stickySlot) {
            stickyIsEmpty = !!event.isEmpty;
            scheduleStickyRefresh();
          }
        });

        googletag.display('tablet-sticky-ad');
        googletag.pubads().refresh([stickySlot]);
      });
    }
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', injectTopAndSkinAdContainers);
    } else {
      injectTopAndSkinAdContainers();
    }
  }

  loadScript(gptUrl)
    .then(() => {
      return loadScriptWithin(prebidUrl, PREBID_LOAD_FAILSAFE_MS)
        .then(() => {
          initializeHomepageDesktopAds();
        })
        .catch((err) => {
          logWarn('Prebid failed to load; continuing with GPT-only ads', err);
          ensurePrebidFallback(pbjs);
          initializeHomepageDesktopAds();
        });
    })
    .catch((err) => {
      logError('Failed to load GPT script (homepage desktop)', err);
      emitAdEvent(emitter, brand, 'error', err);
    });

  // Initialize ad data state and set up insertNewAd function
  if (!window.adData || typeof window.adData.placedAdCount !== 'number') {
    window.adData = { placedAdCount: 1 };
  }

  // Sync counter with any server-rendered or previously-inserted content ads
  syncPlacedAdCountFromDom('dt-content-ad');
  if (typeof document !== 'undefined') {
    const slots = document.querySelectorAll('.content-ad');
    slots.forEach((slot) => ensureAdSlotMinHeight(slot, CONTENT_SLOT_MIN_HEIGHT));
  }

  // Set up insertNewAd function using shared helper
  window.insertNewAd = createHomepageInfeedAdInserter('dt-content-ad', CONTENT_SLOT_MIN_HEIGHT);

  // Set up homepage in-feed ads observer using shared helper
  const infeedConfig = {
    ...getHomepageInfeedConfig(config.brand, 'desktop'),
    minHeight: CONTENT_SLOT_MIN_HEIGHT
  };
  setupHomepageInfeedAdsObserver(infeedConfig);

    emitAdEvent(emitter, brand, 'ready');
  };
}

module.exports = {
  createHomepageDesktopAds
};
